import React, { type JSX } from 'react';

import { useConfirm } from '@fiji/common/src/hooks';
import { CustomModal, CustomTransComponent } from 'components';
import { ModalActions } from 'components/CustomModal';
import { CloneWidgetModal } from '../WidgetList';
import { FormControlLabel, List, ListItem, Radio, Typography } from '@mui/material';
import { useAppDispatch, useTypedSelector } from '@fiji/common';
import { setSaveModalType } from '@fiji/common/src/features/widgetManagement/widgetConfigurationSlice';
import { useWidgetConfigHandlers } from '../common';

export const SaveOverrideModal = React.forwardRef((props: any, ref): JSX.Element => {
    const dispatch = useAppDispatch();

    const {
        handleSaveButton,
        details: { widgetDetails },
    } = useWidgetConfigHandlers();
    const { isVisible, onCancel, onConfirm, onClick } = useConfirm((): Promise<void> => handleSaveButton(onCancel));
    const saveAsModalRef = React.useRef<any>(null);
    const configurationPayload = useTypedSelector((state) => state['widgetConfiguration']);

    const saveModalType = useTypedSelector((state) => state['widgetConfiguration']['saveModalType']);

    const modalActions: ModalActions[] = [
        {
            key: 'cancel',
            testId: 'save-override-cancel-button',

            label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
            variant: 'outlined',
            close: true,
            handleClick: (): void => {
                onCancel();
                dispatch(setSaveModalType(undefined));
            },
        },
        {
            key: 'save',
            testId: 'save-override-confirm-button',
            label: <CustomTransComponent translationKey={'COMMON:SAVE_LABEL'} />,
            variant: 'contained',
            disabled: !saveModalType,
            handleClick: (): void => {
                if (saveModalType === 'update') {
                    onConfirm();
                } else {
                    onCancel();
                    saveAsModalRef?.current?.handleModalAction(true, {
                        ...widgetDetails,
                        sourceId: configurationPayload?.source ? configurationPayload?.source?.id : null,
                        sourceName: configurationPayload?.source ? configurationPayload?.source?.name : null,
                        config: configurationPayload?.data,
                    });
                }
            },
        },
    ];

    React.useImperativeHandle(
        ref,
        () => ({
            openSaveModal: onClick,
        }),
        []
    );

    return (
        <>
            <CustomModal
                actions={modalActions}
                header={<CustomTransComponent translationKey={'WIDGET_MANAGEMENT:SAVE_WIDGET'} />}
                actionsDivider
                isOpen={isVisible}
                type="primary"
                data-testid={props?.['data-testid'] ?? 'save-override-modal'}
            >
                <Typography variant="subtitle1" fontWeight={'600'} my={2}>
                    <CustomTransComponent translationKey={'WIDGET_MANAGEMENT:WIDGETS_DATASOURCE_HAS_BEEN_CHANGED'} />
                </Typography>

                <List className="margin-top-18">
                    <ListItem disablePadding>
                        <FormControlLabel
                            control={
                                <Radio
                                    data-testid="save-as-radio"
                                    checked={saveModalType === 'saveAs'}
                                    onChange={(): void => dispatch(setSaveModalType('saveAs'))}
                                />
                            }
                            label={
                                <Typography variant="body1" className="black-500">
                                    <CustomTransComponent
                                        translationKey={'WIDGET_MANAGEMENT:SAVE_AS_A_NEW_WIDGET_WITH_A_NEW_NAME'}
                                    />
                                </Typography>
                            }
                            className="f-14 fw-400"
                        />
                    </ListItem>
                    <ListItem disablePadding>
                        <FormControlLabel
                            className="f-14 fw-400"
                            control={
                                <Radio
                                    data-testid="override-radio"
                                    checked={saveModalType === 'update'}
                                    onChange={(): void => dispatch(setSaveModalType('update'))}
                                />
                            }
                            label={
                                <Typography variant="body1" className="black-500">
                                    <CustomTransComponent
                                        translationKey={'WIDGET_MANAGEMENT:OVER_RIGHT_THE_EXISTING_WIDGET'}
                                    />
                                </Typography>
                            }
                        />
                    </ListItem>
                </List>
            </CustomModal>
            <CloneWidgetModal data-testid="save-as-clone-widget-modal" ref={saveAsModalRef} modalType="save" />
        </>
    );
});
